import { cn, type ButtonVariant } from '@mypoz/ui';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-white hover:bg-brand-dark',
  secondary: 'bg-surface text-ink border border-line hover:bg-surface-alt',
  ghost: 'bg-transparent text-muted hover:bg-surface-alt hover:text-ink',
  danger: 'bg-danger text-white hover:brightness-95',
};

/**
 * Button styling as a class string, for elements that must not be a <button> — react-router
 * <Link> above all. Nesting an <a> inside a <button> is invalid HTML, so this is the escape
 * hatch rather than an `asChild` prop.
 *
 * Lives outside components/ui.tsx so that file exports components only, which is what keeps
 * Vite's fast refresh working.
 */
export function buttonClass(variant: ButtonVariant = 'primary', className?: string): string {
  return cn(
    'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-md px-4 py-2 lg:min-h-0 lg:min-w-0',
    'text-sm font-medium transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
    VARIANTS[variant],
    className,
  );
}

/**
 * Text/number `<input>` styling, factored out once MYP-37 needed the same string on half a dozen
 * fields in `PackageFormSheet` — before that it was inlined twice and copy-pasting it a further
 * five times was worse than naming it.
 */
export function inputClass(className?: string): string {
  return cn(
    'min-h-11 w-full rounded-md border border-line bg-surface px-3 text-sm text-ink transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-10',
    className,
  );
}

/**
 * Native <select> styling, matching LanguageSwitcher's picker. Native on purpose: correct
 * keyboard and screen-reader behaviour for free, and a real picker sheet on mobile. Pair it with
 * an absolutely-positioned expand_more icon, since `appearance-none` removes the built-in arrow.
 *
 * Since MYP-137 most dropdowns are `Select` from `@mypoz/ui`, whose closed control copies these
 * classes. This stays for the two that are native on purpose: LanguageSwitcher and the phone sort
 * picker in PickerControls.
 */
export function selectClass(className?: string): string {
  return cn(
    'min-h-11 w-full appearance-none rounded-md border border-line bg-surface py-2 pr-9 pl-3',
    'text-sm text-ink transition-colors hover:bg-surface-alt',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-0',
    className,
  );
}

/**
 * An inline call-to-action rendered as text rather than a filled button — the "Create order →"
 * and "View all →" affordances on the Dashboard cards.
 *
 * `min-h-11` holds the 44px touch target on mobile and is released at `lg`, matching the pattern
 * buttonClass uses, so a text link is no harder to tap than a real button.
 */
export function actionLinkClass(className?: string): string {
  return cn(
    'inline-flex min-h-11 items-center gap-1 lg:min-h-0',
    'text-sm font-semibold text-brand transition-colors hover:text-brand-dark',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
    className,
  );
}
